// Archief — soft-delete recovery (design-vs-build Theme 2). The registers soft-delete records but the
// per-entity restore() fns were unrouted + deactivated rows were unreachable. This lists the tenant's
// deactivated records across the recoverable aggregates and restores them. Backend: GET /archive +
// POST /{base}/:id/restore. RLS-scoped; gated verwerking.read (the per-aggregate update is enforced
// server-side on restore).
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import { Can } from "../lib/rbac";
import { useI18n } from "../lib/i18n";
import { PageHeader } from "@/components/page-header";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";

interface ArchivedRecord { id: string; base: string; kind: string; label: string; deletedAt: string | null }

export function ArchiveRoute() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const { data, isLoading, error } = useQuery({ queryKey: ["archive"], queryFn: () => api.listArchive() as Promise<ArchivedRecord[]> });
  const rows = data ?? [];
  const restore = useMutation({
    mutationFn: (r: ArchivedRecord) => api.restoreEntity(r.base, r.id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["archive"] }),
  });

  return (
    <Can perm="verwerking.read" fallback={<PageHeader title={t("nav.archive")} description={t("crud.readonly")} />}>
      <section>
        <PageHeader title={t("nav.archive")} description={t("archive.sub")} />
        {isLoading && <p className="text-sm text-muted-foreground">{t("common.loading")}</p>}
        {error && <p className="text-sm text-destructive">{t("crud.failed")} {(error as Error).message}</p>}
        {restore.isError && <p className="mb-2 text-sm text-destructive">{t("archive.restoreFailed")} {(restore.error as Error).message}</p>}
        {!isLoading && !error && rows.length === 0 && <p className="text-sm text-muted-foreground">{t("archive.empty")}</p>}
        {rows.length > 0 && (
          <Card className="mt-2 p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>{t("archive.col.kind")}</TableHead>
                  <TableHead>{t("archive.col.item")}</TableHead>
                  <TableHead>{t("archive.col.deleted")}</TableHead>
                  <TableHead className="text-right">{t("archive.col.action")}</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.base}:${r.id}`}>
                    <TableCell><Badge variant="secondary">{r.kind}</Badge></TableCell>
                    <TableCell className="font-medium">{r.label}</TableCell>
                    <TableCell className="text-muted-foreground">{r.deletedAt ? new Date(r.deletedAt).toLocaleString() : "—"}</TableCell>
                    <TableCell className="text-right">
                      <Button size="xs" variant="outline" disabled={restore.isPending} onClick={() => restore.mutate(r)}>
                        {restore.isPending && restore.variables?.id === r.id ? "…" : t("archive.restore")}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
        )}
      </section>
    </Can>
  );
}
